import {isPlatformBrowser} from "@angular/common";
import {Router, RouterLink} from "@angular/router";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {AfterViewInit, Component, inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';

import {ToastrService} from "ngx-toastr";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {LogoComponent, PageHeaderComponent} from "@numizmat/components";
import {GoogleLoginRequest, LoginRequest} from "@numizmat/services-models/account";
import {JsonLdService, MetaTagService, ScrollerService} from "@numizmat/services/common";
import {
  AccessTokenService,
  CurrentUserStateService,
  GoogleOAuthApiService,
  LoginApiService
} from "@numizmat/services/account";

import {
  SocialRegistrationModalComponent
} from "@numizmat/pages/account-page/modals/social-registration-modal/social-registration-modal.component";

@Component({
  selector: 'nz-login',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    PageHeaderComponent,
    LogoComponent
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  private platformId: Object = inject(PLATFORM_ID);

  private router: Router = inject(Router);
  private modalService: NgbModal = inject(NgbModal);
  private toastrService: ToastrService = inject(ToastrService);
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private scrollerService: ScrollerService = inject(ScrollerService);
  private loginApiService: LoginApiService = inject(LoginApiService);
  private accessTokenService: AccessTokenService = inject(AccessTokenService);
  private googleOAuthApiService: GoogleOAuthApiService = inject(GoogleOAuthApiService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  protected errorMessage: string = '';
  protected googleLoginLoading: boolean = false;
  protected standardLoginLoading: boolean = false;
  protected form: FormGroup = this.initializeForm();

  protected readonly appRoutes = appRoutes;

  public ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const urlParams = new URLSearchParams(window.location.search);
      const authorizationCode = urlParams.get('code');
      if (!!authorizationCode) {
        this.loginWithGoogle(authorizationCode);
      }
    }
    this.accessTokenService.deleteAccessToken();
    this.currentUserStateService.setCurrentUser({
      loggedIn: false
    });
    this.setSchema();
    this.setMetaTags();
  }

  public ngOnDestroy(): void {
    this.jsonLdService.removeSchema();
  }

  public ngAfterViewInit(): void {
    this.scrollerService.scrollToTop();
  }

  protected onSubmit(): void {
    this.standardLoginLoading = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.standardLoginLoading = false;
      return;
    }
    const formValue = this.form.getRawValue();
    const request: LoginRequest = {
      emailOrUsername: formValue.emailOrUsername,
      password: formValue.password
    };
    this.loginApiService.login(request).subscribe({
      next: response => {
        this.standardLoginLoading = false;
        if (response.success) {
          this.accessTokenService.setAccessToken(response.accessToken);
          this.currentUserStateService.setCurrentUser({
            loggedIn: true,
            userData: {
              userId: response.userId,
              username: response.username,
              newMessageCount: response.newMessageCount,
              newNotificationCount: response.newNotificationCount,
              confirmationMethod: response.confirmationMethod,
              confirmationMethodName: response.confirmationMethodName,
            }
          });
          this.toastrService.success(response.successMessage);
          this.router.navigate([appRoutes.home]).then();
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  protected onClickGoogleLogin(): void {
    const params = new URLSearchParams({
      client_id: environment.googleClientId,
      redirect_uri: environment.googleRedirectUri,
      response_type: 'code',
      scope: 'profile email',
      access_type: 'offline',
      prompt: 'consent'
    });
    window.location.href = `${environment.googleAuthUri}?${params.toString()}`;
  }

  private loginWithGoogle(authorizationCode: string): void {
    this.googleLoginLoading = true;
    const request: GoogleLoginRequest = {
      authorizationCode: authorizationCode
    }
    this.googleOAuthApiService.login(request).subscribe({
      next: response => {
        this.googleLoginLoading = false;
        if (response.success) {
          if (response.isUserRegistered) {
            this.accessTokenService.setAccessToken(response.accessToken);
            this.currentUserStateService.setCurrentUser({
              loggedIn: true,
              userData: {
                userId: response.userId,
                username: response.username,
                newMessageCount: response.newMessageCount,
                newNotificationCount: response.newNotificationCount,
                confirmationMethod: response.confirmationMethod,
                confirmationMethodName: response.confirmationMethodName,
              }
            });
            this.toastrService.success(response.successMessage);
            this.router.navigate([appRoutes.home]).then();
          } else {
            const modalRef: NgbModalRef = this.modalService.open(SocialRegistrationModalComponent, {
              scrollable: true
            });
            modalRef.componentInstance.emailInput = response.email;
            modalRef.componentInstance.usernameInput = response.username;
            modalRef.componentInstance.temporaryTokenInput = response.temporaryToken;
          }
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  private initializeForm() {
    return new FormGroup({
      emailOrUsername: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required])
    });
  }

  private setSchema(): void {
    const loginSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Вход",
      "description": "Страница за вход в профила на Collectoria.",
      "url": `${environment.applicationDomain}/${appRoutes.account}/${appRoutes.login}`
    };
    this.jsonLdService.setSchema(loginSchema);
  }

  private setMetaTags(): void {
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.account}/${appRoutes.login}`);
    this.metaTagService.setTitle('Вход в профила | Collectoria');
    this.metaTagService.setDescription('Вход в профила | Collectoria');
  }

  get emailOrUsername() {
    return this.form.get('emailOrUsername');
  }

  get password() {
    return this.form.get('password');
  }
}
